import { AsyncPipe, DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { ofType } from '@ngrx/effects';
import { ScannedActionsSubject } from '@ngrx/store';
import { CartActions } from '@spartacus/cart/base/core';
import { ActiveCartFacade, Cart, PromotionLocation } from '@spartacus/cart/base/root';
import { CmsService, FeatureConfigService, TranslatePipe, UrlPipe } from '@spartacus/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { map, pairwise, switchMap, take, tap } from 'rxjs/operators';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import {
  GoogleTagManagerEvent,
  GoogleTagManagerService,
} from 'src/app/service/googletagmanager/google-tag-manager.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { SnahCountryService } from 'src/app/service/snah/snah-countries.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { ErpPromotionalTextComponent } from '../../../promotional-text/erp-promotional-text/erp-promotional-text.component';
import { PromotionalTextComponent } from '../../../promotional-text/promotional-text.component';
import { ItemCounterComponent } from '../../item-counter/item-counter.component';
import { MemoComponent } from '../../memo/memo.component';

export interface Item {
  product?: any;
  quantity?: any;
  basePrice?: any;
  totalPrice?: any;
  updateable?: boolean;
  variantInfo?: any;
  entryNumber?: any;
  deliveryDate?: any;
  inventoryLeadTime?: any;
  backordered?: boolean;
  promotionMessage?: any;
  memo?: any;
  unavailableInB2B?: boolean;
}

export interface CartItemComponentOptions {
  isSaveForLater?: boolean;
  optionalBtn?: any;
}

@Component({
  selector: 'cx-cart-item',
  templateUrl: './cart-item.component.html',
  imports: [
    NgClass,
    RouterLink,
    NgTemplateOutlet,
    ErpPromotionalTextComponent,
    ItemCounterComponent,
    MatIcon,
    MatTooltip,
    MemoComponent,
    PromotionalTextComponent,
    AsyncPipe,
    DatePipe,
    UrlPipe,
    TranslatePipe,
  ],
})
export class CartItemComponent implements OnInit, OnDestroy {
  @Input() item: Item;
  @Input() pageId: string;
  @Input() potentialProductPromotions: any[];
  @Input() readonly = false;
  @Input() quantityControl: FormControl;
  @Input() memoControl: FormControl;
  @Input() lastItem: boolean;
  @Input() noStockError = false;
  @Input() lowStockError = false;
  @Input() dateFormatted: string;
  @Input() skipVariantCategory: string;
  @Input() cartPageId: string;
  @Input() unapprovedError = false;

  @Output() view = new EventEmitter<any>();

  @Input() promotionLocation: PromotionLocation = PromotionLocation.ActiveCart;

  // TODO: evaluate whether this is generic enough
  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null,
  };

  @Input() dealerSpinner = false;

  @Input() removeSpinner = false;

  @Input() appliedPromotions: any = [];

  @Input() showWarehouseFeature: boolean;

  promotionalTextMessage: string;

  quantity = 1;

  showSrp = false;
  srpSubscription$: Subscription;
  currentPageSubscription$: Subscription;
  spinnerSubscription$: Subscription;
  cartSubscription$: Subscription;
  isCheckoutPage = false;
  minOrderQuantity = 1;
  isMinQty = true;
  cartUpdateEntrySuccessSubscription$: Subscription;
  cartUpdateEntry = false;
  cartRemoveEntry = false;
  initItemQuantity = 0;
  onRemoveItemClicked$ = new BehaviorSubject<boolean>(false);
  isMemoValid = true;
  isInputValid = true;
  isLimitValid = true;
  janCodeSubscription: Subscription;
  shimanoMemoEnabledSubscription: Subscription;
  shimanoMemoCartEditableSubscription: Subscription;
  shimanoMemoCheckoutEditableSubscription: Subscription;
  janCodeEnabled: boolean;
  shimanoMemoEnabled: boolean;
  shimanoMemoCartEditable: boolean;
  shimanoMemoCheckoutEditable: boolean;
  cart$: Observable<Cart>;
  businessType: string;
  isManualAllocationActive: boolean = false;

  categories: any = {};
  gtmbusinessType: string = this.currentBusinesstype.getBusinessType();
  affiliation = `/${this.gtmbusinessType}/${this.gtmActiveCountry}/${this.activeLanguage}`;

  activeCountry$: Observable<string> = this.countryService.getActive();
  showGstExclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_EXCLUSIVE)
    )
  );

  constructor(
    protected cartService: ActiveCartFacade,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private cdr: ChangeDetectorRef,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService,
    private customProductService: CustomProductService,
    private SnahCountryService: SnahCountryService,
    private featureConfig?: FeatureConfigService,
    private actionsSubject?: ScannedActionsSubject,
    private gtmService?: GoogleTagManagerService,
    private countryService?: ShimanoCountryService,
    private checkoutServiceExtends?: CheckoutServiceExtends,
    private currentBusinesstype?: CurrentBusinessType,
    public deviceDetectorService?: DeviceDetectorService,
    private languageService?: ShimanoLanguageService
  ) {
    this.cart$ = this.cartService.getActive();
  }

  get gtmActiveCountry(): string {
    let activeCountry: string;
    this.countryService.getActive().subscribe((country) => (activeCountry = country));
    return activeCountry;
  }

  get activeLanguage(): string {
    let activeLanguage: string;
    this.languageService.getActive().subscribe((lang) => (activeLanguage = lang));
    return activeLanguage;
  }

  ngOnInit() {
    this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && !currentPage.template.includes('ShimanoCheckoutSummaryPageTemplate')) {
        this.storageService.removeSessionStorageItem('saddID');
        this.storageService.removeSessionStorageItem('saddFormatted');
        this.storageService.removeSessionStorageItem('baddID');
        this.storageService.removeSessionStorageItem('baddFormatted');
      }
    });

    if (this.appliedPromotions.length > 0) {
      this.appliedPromotions.filter((data: any) => {
        data.consumedEntries[0].orderEntryNumber === this.item.entryNumber;
      });
    }

    this.customProductService
      .isProductManualAllocationEnabled$()
      .pipe(take(1))
      .subscribe((isEnabled) => (this.isManualAllocationActive = isEnabled && this.item?.product?.manualAllocation));

    this.getInitSrpToggleValue();

    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.spinnerSubscription$ = this.componentService.dealerPriceSpinner$.subscribe((code) => {
      this.dealerSpinner = true;
      this.componentService.setMiniCartLoading(true);
      this.cdr.detectChanges();
    });

    if (this.item.product.minOrderQuantity) {
      this.minOrderQuantity = this.item.product.minOrderQuantity;
    }

    if (this.storageService.getSessionStorageItem('MOQCartError') !== null) {
      const items = JSON.parse(this.storageService.getSessionStorageItem('MOQCartError'));
      if (items.indexOf(this.item.product.code) >= 0) {
        this.isMinQty = false;
      }
    }
    combineLatest([
      this.cartService.isStable(),
      this.actionsSubject.pipe(
        ofType(
          CartActions.CART_UPDATE_ENTRY_SUCCESS,
          CartActions.CART_UPDATE_ENTRY_FAIL,
          CartActions.CART_REMOVE_ENTRY_SUCCESS,
          CartActions.CART_REMOVE_ENTRY_FAIL,
          CartActions.DELETE_CART,
          CartActions.DELETE_CART_FAIL
        )
      ),
      this.componentService.cartEntries$,
    ])
      .pipe(
        tap(([cartLoaded, cartAction, cartEntries]: [boolean, any, any[]]) => {
          if (cartLoaded) {
            switch (cartAction.type) {
              case CartActions.CART_UPDATE_ENTRY_SUCCESS:
                if (this.componentService.isItemCounterChanged$.getValue()) {
                  let prevCartEntries: any = [];
                  let nextCartEntries: any = [];
                  if (cartEntries.length > 1) {
                    prevCartEntries = cartEntries[cartEntries.length - 2];
                    nextCartEntries = cartEntries[cartEntries.length - 1];
                    nextCartEntries.forEach((nextCartEntry: any, index: any) => {
                      if (prevCartEntries && prevCartEntries[index]) {
                        const quantityChanged = nextCartEntry.quantity - prevCartEntries[index].quantity;
                        this.gtmUpdateCartEntry(nextCartEntry, Math.abs(quantityChanged), quantityChanged > 0);
                      }
                    });
                    if (prevCartEntries.length > nextCartEntries.length) {
                      this.gtmUpdateCartEntry(
                        prevCartEntries[prevCartEntries.length - 1],
                        prevCartEntries[prevCartEntries.length - 1].quantity,
                        false
                      );
                    }
                    if (nextCartEntries.length > prevCartEntries.length) {
                      this.gtmUpdateCartEntry(
                        nextCartEntries[nextCartEntries.length - 1],
                        nextCartEntries[nextCartEntries.length - 1].quantity,
                        true
                      );
                    }
                  }
                  this.componentService.isItemCounterChanged$.next(false);
                }
                break;
              case CartActions.CART_REMOVE_ENTRY_SUCCESS:
                if (this.onRemoveItemClicked$.getValue()) {
                  this.gtmUpdateCartEntry(this.item, this.item.quantity, false);
                  this.onRemoveItemClicked$.next(false);
                }
                break;
              case CartActions.DELETE_CART:
                if (this.componentService.onDeleteCart$.getValue()) {
                  this.gtmDeleteCartEntries(cartEntries);
                  this.componentService.onDeleteCart$.next(false);
                }
                break;
            }
          }
        })
      )
      .subscribe();
    this.businessType = this.currentBusinesstype.getBusinessType();

    this.isJanCodeEnabled();

    this.isShimanoMemoEnabled();

    this.isShimanoMemoCartEditable();

    this.isShimanoMemoCheckoutEditable();
  }

  // TODO remove feature flag for #5958
  isSaveForLaterEnabled(): boolean {
    if (this.featureConfig) {
      return this.featureConfig.isEnabled('saveForLater');
    }
    return false;
  }
  // TODO remove feature flag for #5958

  isProductOutOfStock(product: any) {
    // TODO Move stocklevelstatuses across the app to an enum
    return product && product.stock && product.stock.stockLevelStatus === 'outOfStock';
  }

  removeItem(item: Item) {
    this.removeSpinner = true;
    document.querySelector('cx-page-layout.CartPageTemplate').classList.add('rotating');
    document.documentElement.style.overflow = 'hidden';
    this.cartService.removeEntry(item);
    this.cdr.detectChanges();
    this.noStockError = typeof this.noStockError === 'undefined' ? false : this.noStockError;
    this.unapprovedError = typeof this.unapprovedError === 'undefined' ? false : this.unapprovedError;
    if (
      (this.noStockError &&
        document.getElementsByClassName('no-stock-error').length <= 1 &&
        document.getElementsByClassName('unapproved-error').length === 0) ||
      (this.unapprovedError &&
        document.getElementsByClassName('unapproved-error').length <= 1 &&
        document.getElementsByClassName('no-stock-error').length === 0)
    ) {
      this.componentService.setNoStockError(true);
    }
    this.onRemoveItemClicked$.next(true);
    this.cartSubscription$ = this.cartService
      .getLoading()
      .pipe(pairwise())
      .subscribe(([prevLoadingState, currLoadingState]) => {
        // remove rotating class if cart is stable
        if (prevLoadingState && !currLoadingState) {
          document.querySelector('cx-page-layout.CartPageTemplate').classList.remove('rotating');
        }
      });
  }

  viewItem() {
    this.view.emit();
  }

  updateCount(qty: number): void {
    if (qty) {
      this.quantityControl.setValue(qty);
      this.quantityControl.markAsDirty();
    }
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  isReadOnlyInput(
    pageId: string,
    noStock: boolean,
    updateable: boolean,
    unavailable: boolean,
    unapprovedError: boolean
  ) {
    if (pageId === this.cartPageId) {
      if (!updateable) {
        return true;
      } else if (noStock) {
        return true;
      } else if (unavailable && unapprovedError) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(80);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  checkVariant(variants: any) {
    return variants
      .filter((variant: any) => {
        if (variant.code !== this.skipVariantCategory) {
          return variant;
        }
      })
      .slice(0, 3);
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  toZeroPrice(data: any) {
    if (!data) {
      return '';
    } else {
      return `${data.slice(0, 2)}0.00`;
    }
  }

  gtmUpdateCartEntry(entry: any, quantity: number, add: boolean) {
    this.categories = {};
    entry.product.categories.forEach((cat: any, i: any) => {
      const indexCount = i + 1;
      const label = `item_category${indexCount == 1 ? '' : indexCount}`;
      this.categories[label] = cat.name;
    });
    const totalPrice = entry.basePrice.value * quantity;
    const gtmTagUpdateCart = {
      event: add ? GoogleTagManagerEvent.ADD_TO_CART : GoogleTagManagerEvent.REMOVE_FROM_CART,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        value: totalPrice.toFixed(2),
        items: [
          {
            item_id: entry.product.code,
            item_name: entry.product.name,
            affiliation: this.affiliation,
            item_brand: entry?.product?.brand?.name,
            ...this.categories,
            price: entry.basePrice.formattedValue,
            quantity,
          },
        ],
      },
    };
    if (quantity) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTagUpdateCart);
    }
  }

  gtmDeleteCartEntries(entries: any[]) {
    const productsList: any[] = [];
    let totalPrice = 0;

    entries.forEach((entry) => {
      let totalPriceNoDiscount = 0;
      let discount = 0;
      this.categories = {};

      if (entry) {
        entry.product.categories.forEach((cat: any, i: any) => {
          const indexCount = i + 1;
          const label = `item_category${indexCount == 1 ? '' : indexCount}`;
          this.categories[label] = cat.name;
        });

        totalPriceNoDiscount = entry.basePrice.value * entry.quantity;
        discount = totalPriceNoDiscount - entry.totalPrice.value;
        totalPrice = totalPrice + entry.totalPrice.value;

        productsList.push({
          item_id: entry.product.code,
          item_name: entry.product.name,
          affiliation: this.affiliation,
          coupon: entry.promotionCode ? entry.promotionCode : '',
          item_brand: entry?.product?.brand?.name,
          ...this.categories,
          price: entry.basePrice.formattedValue,
          discount: discount == 0 ? '0' : discount.toFixed(2),
          quantity: entry.quantity,
        });
      }
    });

    const gtmTag = {
      event: GoogleTagManagerEvent.REMOVE_FROM_CART,
      ecommerce: {
        currency: this.storageService.getSessionStorageItem('currency'),
        value: totalPrice.toFixed(2),
        items: productsList,
      },
    };
    if (productsList.length) {
      this.gtmService.pushTag({
        ecommerce: null,
        search_term: '',
        results_page: '',
        affiliation: '',
        content_type: '',
        content_id: '',
        content_name: '',
      });
      this.gtmService.pushTag(gtmTag);
    }
  }

  isDiscounted(totalPrice: number, basePrice: number, quantity: number, appliedProductPromotions: any[]): boolean {
    appliedProductPromotions =
      appliedProductPromotions === null || typeof appliedProductPromotions === 'undefined'
        ? []
        : appliedProductPromotions;
    const originalPrice = Math.round((basePrice * quantity + Number.EPSILON) * 100) / 100;
    return totalPrice < originalPrice && !!appliedProductPromotions.length;
  }

  isMemoEnabled(noStock: boolean): boolean {
    if (this.pageId !== this.cartPageId) {
      if (this.shimanoMemoCheckoutEditable === true) {
        return false;
      } else {
        return true;
      }
    } else {
      if (noStock) {
        return this.shimanoMemoCartEditable;
      }
      return false;
    }
  }

  isJanCodeEnabled = () => {
    this.janCodeSubscription = this.getJanCodeConfig$().subscribe((janCode) => {
      this.janCodeEnabled = janCode === 'true';
      this.cdr.detectChanges();
    });
  };

  getJanCodeConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.ean.display.cart.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.ean.display.cart.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoEnabled = () => {
    this.shimanoMemoEnabledSubscription = this.getShimanoMemoConfig$().subscribe((memoEnabled) => {
      this.shimanoMemoEnabled = memoEnabled === 'true';
      this.cdr.detectChanges();
    });
  };

  getShimanoMemoConfig$(): Observable<string> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs.filter((config: any) => config.key === 'shimano.memo.enabled').map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoCartEditable = () => {
    this.shimanoMemoCartEditableSubscription = this.getCartEditableConfig$().subscribe((cartEditable) => {
      this.shimanoMemoCartEditable = cartEditable;
      this.cdr.detectChanges();
    });
  };

  getCartEditableConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.cart.editable.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.cart.editable.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  isShimanoMemoCheckoutEditable = () => {
    this.shimanoMemoCheckoutEditableSubscription = this.getCheckoutEditableConfig$().subscribe((checkoutEditable) => {
      this.shimanoMemoCheckoutEditable = checkoutEditable;
      this.cdr.detectChanges();
    });
  };

  getCheckoutEditableConfig$(): Observable<boolean> {
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$(['shimano.memo.checkout.editable.enabled'])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs
              .filter((config: any) => config.key === 'shimano.memo.checkout.editable.enabled')
              .map((res: any) => res.value)[0]
        )
      );
  }

  checkLeadTime(leadTime: any) {
    const reWhiteSpace = new RegExp('\\s+');
    let inventoryLeadTime;

    if (reWhiteSpace.test(leadTime)) {
      inventoryLeadTime = leadTime.trim();
    } else {
      inventoryLeadTime = leadTime;
    }

    return inventoryLeadTime && inventoryLeadTime !== '0' ? true : false;
  }

  ngOnDestroy() {
    this.srpSubscription$?.unsubscribe();
    this.currentPageSubscription$?.unsubscribe();
    this.spinnerSubscription$?.unsubscribe();
    this.janCodeSubscription?.unsubscribe();
    this.cartSubscription$?.unsubscribe();
  }
}
